// frontend/hooks/useAdminMembers.ts

import { useState, useCallback } from "react";
import { authHeaders } from "../utils/api";

interface CreatedInfo {
  loginId: string;
  tempPass: string;
  isReset?: boolean;
}

export const useAdminMembers = (role: string, accessToken: string | undefined, apiUrl: string) => {
  const [members, setMembers] = useState<any[]>([]);
  const [createdInfo, setCreatedInfo] = useState<CreatedInfo | null>(null);

  // 멤버 목록 조회
  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/auth/members`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ requesterRole: role }),
      });
      if (res.ok) setMembers(await res.json());
    } catch (error) {
      console.error("[AlphaNote:Settings] 멤버 로딩 실패:", error);
    }
  }, [apiUrl, role, accessToken]);

  // 신규 멤버 생성
  const createMember = async (memberData: any) => {
    setCreatedInfo(null);
    try {
      const res = await fetch(`${apiUrl}/auth/create-member`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify(memberData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "생성 실패");

      setCreatedInfo({ loginId: data.loginId, tempPass: data.tempPassword });
      fetchMembers();
      return true;
    } catch (error: any) {
      alert(error.message);
      return false;
    }
  };

  // 비밀번호 강제 초기화
  const resetPassword = async (targetLoginId: string) => {
    if (!confirm(`${targetLoginId}의 비밀번호를 강제 초기화하시겠습니까?`)) return;
    setCreatedInfo(null);
    try {
      const res = await fetch(`${apiUrl}/auth/reset-password-admin`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ targetLoginId, requesterRole: role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error("초기화 실패");

      setCreatedInfo({ loginId: targetLoginId, tempPass: data.tempPassword, isReset: true });
    } catch (err: any) {
      alert(err.message);
    }
  };

  // 멤버 조직 정보 수정 (소속·직책·부서장 여부)
  const updateMember = async (memberId: string, data: { department?: string; position?: string; isDeptHead?: boolean }) => {
    try {
      const res = await fetch(`${apiUrl}/auth/members/${memberId}`, {
        method: "PATCH",
        headers: authHeaders(accessToken),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("수정 실패");
      await fetchMembers();
      return true;
    } catch (error: any) {
      alert(error.message);
      return false;
    }
  };

  // 계정 삭제 및 문서 인계
  const deleteMember = async (memberId: string) => {
    try {
      const res = await fetch(`${apiUrl}/auth/members/${memberId}`, {
        method: "DELETE",
        headers: authHeaders(accessToken),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "계정 삭제 실패");
      }
      await fetchMembers();
    } catch (error: any) {
      alert(error.message);
      throw error;
    }
  };

  return { members, fetchMembers, createMember, resetPassword, updateMember, deleteMember, createdInfo, setCreatedInfo };
};
